// Skip-logic route evaluator (browser side).
//
// This is the TypeScript/JS twin of the server's C# evaluator. Both are pinned
// by one shared fixture, routing-cases.json, which is copied verbatim from
// Custom-Api. When a case fails here the two evaluators disagree: fix this
// evaluator, never the fixture.
//
// resolveRoute walks the survey's items in order, decides each skip-logic
// section from its rules, and returns the items actually shown plus a trace of
// every section it reached. It has no React or API dependency.
//
// The algorithm is specified in __fixtures__/routing-cases.schema.md.

// Question types, as the API serialises them.
const SLIDER = 1
const LIKERT = 2
const PERCENTAGE = 3

const CHOICE_TYPES = new Set([LIKERT, 4, 5, 7]) // Likert, yes/no, yes/no/maybe, multiple choice
const MULTI_TYPES = new Set([6])
const NUMERIC_TYPES = new Set([SLIDER, LIKERT, PERCENTAGE])
const TESTABLE_TYPES = new Set([1, 2, 3, 4, 5, 6, 7]) // not ranking (8) or comments (9)

const CHOICE_OPS = new Set(['is', 'isNot', 'isAnyOf', 'isNoneOf'])
const MULTI_OPS = new Set([
  'includesAnyOf',
  'includesAllOf',
  'includesNoneOf',
  'includesExactly',
])
const NUMERIC_OPS = new Set(['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'between'])

// Which operators a question type accepts. An operator used on a type it isn't
// listed for never holds (and the server reports condition-unsupported).
const operatorAppliesTo = (op, type) => {
  if (op === 'notAnswered') return TESTABLE_TYPES.has(type)
  if (CHOICE_OPS.has(op)) return CHOICE_TYPES.has(type)
  if (MULTI_OPS.has(op)) return MULTI_TYPES.has(type)
  if (NUMERIC_OPS.has(op)) return NUMERIC_TYPES.has(type)
  return false
}

// An answer counts as given once it carries a value or a number. Option value 0
// and a numeric answer of 0 are both real, so test for presence, not truthiness.
const hasAnswer = a =>
  !!a && ((Array.isArray(a.values) && a.values.length > 0) || a.number != null)

// The number a numeric operator compares against. Slider/percentage carry it
// directly; Likert derives it from the chosen option (its numValue when the
// question has custom values - a null custom value counts as 0 - otherwise its
// order). A Likert value that is not one of the options has no number.
const numberForAnswer = (item, answer) => {
  const type = item.questionType
  if (type === SLIDER || type === PERCENTAGE) {
    return answer.number != null ? answer.number : null
  }
  if (type === LIKERT) {
    const chosen = (answer.values || [])[0]
    if (chosen == null) return null
    const opt = (item.options || []).find(o => o.value === chosen)
    if (!opt) return null
    if (item.hasCustomValue) return opt.numValue == null ? 0 : opt.numValue
    return opt.order
  }
  return null
}

const compareAnswer = (cond, item, answer) => {
  const op = cond.op
  const chosen = answer.values || []
  const values = cond.values || []

  if (CHOICE_OPS.has(op)) {
    if (op === 'is') return chosen.includes(values[0])
    if (op === 'isNot') return !chosen.includes(values[0])
    if (op === 'isAnyOf') return values.some(v => chosen.includes(v))
    if (op === 'isNoneOf') return !values.some(v => chosen.includes(v))
  }

  if (MULTI_OPS.has(op)) {
    if (op === 'includesAnyOf') return values.some(v => chosen.includes(v))
    if (op === 'includesAllOf') return values.every(v => chosen.includes(v))
    if (op === 'includesNoneOf') return !values.some(v => chosen.includes(v))
    if (op === 'includesExactly') {
      const picked = new Set(chosen)
      const wanted = new Set(values)
      if (picked.size !== wanted.size) return false
      for (const v of wanted) if (!picked.has(v)) return false
      return true
    }
  }

  if (NUMERIC_OPS.has(op)) {
    const n = numberForAnswer(item, answer)
    if (n == null) return false
    if (op === 'eq') return n === cond.number
    if (op === 'ne') return n !== cond.number
    if (op === 'gt') return n > cond.number
    if (op === 'gte') return n >= cond.number
    if (op === 'lt') return n < cond.number
    if (op === 'lte') return n <= cond.number
    if (op === 'between') return n >= cond.min && n <= cond.max
  }

  return false
}

// An answer condition, checked in the order the schema pins: valid subject +
// operator first (else false whatever the operator, notAnswered included), then
// unanswered/off-route (notAnswered true, all else false), then the comparison.
const evalAnswer = (answer, ctx) => {
  const item = ctx.itemById.get(answer.item)
  if (!item || item.kind !== 0 || item.questionType == null) return false
  if (!operatorAppliesTo(answer.op, item.questionType)) return false

  const given = ctx.answers[String(answer.item)]
  const answered = ctx.shownQuestions.has(answer.item) && hasAnswer(given)
  if (!answered) return answer.op === 'notAnswered'
  if (answer.op === 'notAnswered') return false

  return compareAnswer(answer, item, given)
}

const evalGroup = (group, ctx) => {
  if (!group || typeof group !== 'object') return false
  if (Array.isArray(group.all)) return group.all.every(c => evalCondition(c, ctx))
  if (Array.isArray(group.any)) return group.any.some(c => evalCondition(c, ctx))
  return false
}

const evalCondition = (cond, ctx) => {
  if (!cond || typeof cond !== 'object') return false
  if (Array.isArray(cond.all) || Array.isArray(cond.any)) return evalGroup(cond, ctx)
  if (cond.answer) return evalAnswer(cond.answer, ctx)
  // A fact stands in for a participant/attribute condition the server resolved.
  if (cond.fact != null) return ctx.participantFacts[cond.fact] === true
  // participant/attribute conditions (and anything unknown) can't be evaluated
  // in the browser, so they never hold here.
  return false
}

// version 1 is the only rules shape the evaluator reads. Anything else makes a
// section with no rules, so the walk falls through to the next item.
const readRules = section => {
  const r = section.rules
  if (!r || r.version !== 1 || !Array.isArray(r.rules)) return null
  return r
}

// Resolve a destination against the items after the section. next and end are
// always valid; an item destination must be a later question or content screen.
const resolveDestination = (dest, sectionPos, ctx) => {
  if (dest && dest.end === true) return {ok: true, destination: {end: true}, stop: true}
  if (dest && dest.next === true) {
    return {ok: true, destination: {next: true}, nextPos: sectionPos + 1}
  }
  if (dest && dest.item != null) {
    const idx = ctx.indexById.get(dest.item)
    if (idx === undefined) return {ok: false, problem: 'destination-missing'}
    if (ctx.items[idx].kind === 2) return {ok: false, problem: 'destination-is-skip-logic'}
    if (idx <= sectionPos) return {ok: false, problem: 'destination-not-forward'}
    return {ok: true, destination: {item: dest.item}, nextPos: idx}
  }
  return {ok: false, problem: 'destination-missing'}
}

// Decide one skip-logic section: the first rule that holds wins, otherwise the
// section's otherwise applies. A refused destination falls back to otherwise,
// and a refused otherwise sends the walk on to the next item. fallback records
// the first destination refused.
const decideSection = (section, sectionPos, ctx) => {
  const base = {section: section.id}
  const rulesObj = readRules(section)
  if (!rulesObj) {
    return {
      ...base,
      matchedRule: null,
      destination: {next: true},
      fallback: 'rules-unreadable',
      nextPos: sectionPos + 1,
      stop: false,
    }
  }

  let matchedRule = null
  let chosen = rulesObj.otherwise
  for (const rule of rulesObj.rules) {
    if (evalGroup(rule.when, ctx)) {
      matchedRule = rule.id
      chosen = rule.goTo
      break
    }
  }

  const primary = resolveDestination(chosen, sectionPos, ctx)
  if (primary.ok) {
    return {...base, matchedRule, destination: primary.destination, fallback: null, nextPos: primary.nextPos, stop: !!primary.stop}
  }

  const fallback = primary.problem
  if (matchedRule !== null) {
    const other = resolveDestination(rulesObj.otherwise, sectionPos, ctx)
    if (other.ok) {
      return {...base, matchedRule, destination: other.destination, fallback, nextPos: other.nextPos, stop: !!other.stop}
    }
  }
  return {...base, matchedRule, destination: {next: true}, fallback, nextPos: sectionPos + 1, stop: false}
}

// A condition's result for the route-trace panel, recorded with the same
// evaluation the route uses.
const explainCondition = (cond, ctx) => {
  const held = evalCondition(cond, ctx)
  if (cond?.all || cond?.any) {
    const mode = cond.all ? 'all' : 'any'
    return {kind: 'group', mode, held, conditions: cond[mode].map(c => explainCondition(c, ctx))}
  }
  if (cond?.answer) {
    const {item, op, values, number, min, max} = cond.answer
    return {kind: 'answer', held, item, op, values, number, min, max}
  }
  if (cond?.participant) {
    return {kind: 'participant', held, op: cond.participant.op, values: cond.participant.values}
  }
  if (cond?.attribute) {
    return {kind: 'attribute', held, key: cond.attribute.key, op: cond.attribute.op, values: cond.attribute.values}
  }
  if (cond?.fact != null) return {kind: 'fact', held, fact: cond.fact}
  return {kind: 'unknown', held}
}

// One section's detail: the authoritative decision (reused from decideSection)
// plus every rule's match state and condition breakdown, so the author can see
// which rule, or the otherwise route, decided the destination.
const explainSection = (section, sectionPos, ctx) => {
  const decided = decideSection(section, sectionPos, ctx)
  const rulesObj = readRules(section)
  const rules = (rulesObj?.rules || []).map(rule => ({
    id: rule.id,
    matched: evalGroup(rule.when, ctx),
    when: explainCondition(rule.when, ctx),
    goTo: rule.goTo,
  }))
  return {
    detail: {
      section: decided.section,
      rules,
      matchedRule: decided.matchedRule,
      destination: decided.destination,
      fallback: decided.fallback,
      unreadable: rulesObj == null,
    },
    nextPos: decided.nextPos,
    stop: decided.stop,
  }
}

// Walk the items, resolving each skip-logic section, and return the shown item
// ids in order plus one trace entry per section reached.
export const resolveRoute = (items = [], answers = {}, participantFacts = {}) => {
  const indexById = new Map(items.map((item, i) => [item.id, i]))
  const itemById = new Map(items.map(item => [item.id, item]))
  const ctx = {
    items,
    answers: answers || {},
    participantFacts: participantFacts || {},
    indexById,
    itemById,
    shownQuestions: new Set(),
  }

  const shownItemIds = []
  const trace = []
  let pos = 0
  // Section destinations are forward-only, so pos never moves back; the guard
  // is a backstop against a malformed fixture, not expected to fire.
  let guard = items.length * 2 + 10

  while (pos >= 0 && pos < items.length && guard-- > 0) {
    const item = items[pos]
    if (item.kind === 2) {
      const decided = decideSection(item, pos, ctx)
      trace.push({
        section: decided.section,
        matchedRule: decided.matchedRule,
        destination: decided.destination,
        fallback: decided.fallback,
      })
      if (decided.stop) break
      pos = decided.nextPos
      continue
    }
    shownItemIds.push(item.id)
    if (item.kind === 0) ctx.shownQuestions.add(item.id)
    pos += 1
  }

  return {shownItemIds, trace}
}

// Like resolveRoute, but returns a detailed per-section trace for the preview's
// route-trace panel: the items shown, the items skipped, and for each section
// reached, every rule's match state + condition results and the chosen
// destination. Uses the same evaluation as resolveRoute, so it always agrees.
export const explainRoute = (items = [], answers = {}, participantFacts = {}) => {
  const indexById = new Map(items.map((item, i) => [item.id, i]))
  const itemById = new Map(items.map(item => [item.id, item]))
  const ctx = {
    items,
    answers: answers || {},
    participantFacts: participantFacts || {},
    indexById,
    itemById,
    shownQuestions: new Set(),
  }

  const shownItemIds = []
  const sections = []
  let pos = 0
  let guard = items.length * 2 + 10

  while (pos >= 0 && pos < items.length && guard-- > 0) {
    const item = items[pos]
    if (item.kind === 2) {
      const explained = explainSection(item, pos, ctx)
      sections.push(explained.detail)
      if (explained.stop) break
      pos = explained.nextPos
      continue
    }
    shownItemIds.push(item.id)
    if (item.kind === 0) ctx.shownQuestions.add(item.id)
    pos += 1
  }

  const shown = new Set(shownItemIds)
  const skippedItemIds = items
    .filter(item => item.kind !== 2 && !shown.has(item.id))
    .map(item => item.id)

  return {shownItemIds, skippedItemIds, sections}
}

export default resolveRoute
